// Guia "Como usar" (texto 08, C1): o destaque do "▶ Mostrar na tela", montado UMA vez na raiz do app (app/_layout.tsx).
// Abre a tela real, espera o ponto marcado (<AlvoGuia> / useAlvoGuia) aparecer, e mostra a setinha animada e o balão.
// Um toque fora ou "Entendi" fecha e oferece "Voltar ao guia". Aba sem acesso: mostra a explicação com 🔒 (sem abrir a aba).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Animated, Easing, Modal, Platform, Pressable, Text, View, useWindowDimensions, type StyleProp, type ViewStyle } from 'react-native';
import { router } from 'expo-router';
import { C, useCores } from '../theme';
import { t } from '../../lib/i18n';
import { ALVOS, type Alvo } from '../../lib/guia/alvos';
import { plano } from '../../lib/guia/conteudo';
import { acharPonto, fecharPedido, mostrarNaTela, pedirFoco, pedirPaginasAjustes, useAlvoGuia, usePedidoDestaque, type Retangulo, type Volta } from '../../lib/guia/destaque';
import { aoDesbloquearAba, precisaSenhaAba } from '../../lib/travaAba';
import type { AbaId } from '../../store/types';
import Paywall from '../../screens/Paywall';

const NATIVO = Platform.OS !== 'web';

/** Marca um elemento da tela como alvo do guia, envolvendo-o numa View sem estilo (collapsable={false}).
 *  Onde a View a mais mudaria o layout (ex.: filho com flex numa linha), passe o estilo de layout em `style`
 *  ou use o hook useAlvoGuia(id) direto no elemento (ref={a.ref}). */
export function AlvoGuia({ id, children, style }: { id: string; children: ReactNode; style?: StyleProp<ViewStyle> }) {
  const a = useAlvoGuia(id);
  return <View ref={a.ref} collapsable={false} style={style}>{children}</View>;
}

/** Abre uma aba como a tela Abas faz. */
export function abrirAba(id: AbaId, params?: Record<string, string>) {
  if (id === 'planner') return router.navigate('/');
  if (id === 'anotacoes') return router.navigate('/anotacoes');
  router.navigate({ pathname: '/aba/[id]', params: { ...(params ?? {}), id } } as any);
}

/** Páginas de Ajustes para voltar a uma página do guia (as do topo ficam na própria tela Como usar). */
export const pilhaDoGuia = (pagina: string) => ['comousar', ...(['gestos', 'primeiros', 'perguntas'].includes(pagina) ? [] : ['guia:' + pagina])];

/** "Voltar ao guia": reabre a mesma página e seção. */
export function voltarAoGuia(v: Volta) {
  pedirPaginasAjustes(pilhaDoGuia(v.pagina));
  pedirFoco(v);
  router.navigate('/ajustes' as any);
}

function irPara(alvo: Alvo) {
  if (alvo.ajustes) { pedirPaginasAjustes(alvo.ajustes); router.navigate('/ajustes' as any); return; }
  if (alvo.aba) return abrirAba(alvo.aba, alvo.params);
  if (alvo.rota) router.navigate(alvo.rota as any);
}

type Fase =
  | { tipo: 'buscando' }
  | { tipo: 'achou'; r: Retangulo; texto: string }
  | { tipo: 'meio'; texto: string; aviso?: string }
  | { tipo: 'bloqueada'; aba: AbaId };

export function DestaqueGuia() {
  const pedido = usePedidoDestaque();
  const { liberada, accent } = useCores();
  const { width: W, height: H } = useWindowDimensions();
  const [fase, setFase] = useState<Fase | null>(null);
  const [volta, setVolta] = useState<Volta | null>(null);
  const raiz = useRef<View>(null);
  const vez = useRef(0);

  useEffect(() => {
    if (!pedido) return;
    const n = ++vez.current;
    const vivo = () => vez.current === n;
    setVolta(null);
    const alvo = ALVOS[pedido.alvo];
    const texto = alvo?.balao ? plano(t(alvo.balao)) : (pedido.texto ?? '');
    if (!alvo) { setFase({ tipo: 'meio', texto, aviso: t('Este ponto ainda não está marcado no app.') }); return; }
    if (alvo.aba && !liberada(alvo.aba)) { setFase({ tipo: 'bloqueada', aba: alvo.aba }); return; }
    let precisa = false;
    try { precisa = !!alvo.precondicao && !alvo.precondicao(); } catch { /* sem como conferir: segue */ }
    // texto 10 (D): aba com senha trancada — abre a aba (com a trava) e avisa; depois de desbloquear, o guia continua sozinho
    if (alvo.aba && !alvo.ajustes && precisaSenhaAba(alvo.aba)) {
      const p = pedido;
      irPara(alvo);
      aoDesbloquearAba(alvo.aba, () => mostrarNaTela(p.alvo, { texto: p.texto, volta: p.volta }));
      setTimeout(() => vivo() && setFase({ tipo: 'meio', texto, aviso: t('Esta aba pede senha. Desbloqueie a aba primeiro e o guia continua daqui.') }), 500);
      return;
    }
    irPara(alvo);
    try { alvo.antes?.(); } catch { /* segue */ }
    if (alvo.soNoApp && Platform.OS === 'web') { setTimeout(() => vivo() && setFase({ tipo: 'meio', texto, aviso: t('Disponível no app do celular.') }), 500); return; }
    if (precisa) { setTimeout(() => vivo() && setFase({ tipo: 'meio', texto: plano(t(alvo.precondicaoTexto ?? '')) || texto }), 500); return; }
    const elemento = alvo.elemento === false ? null : (alvo.elemento ?? pedido.alvo);
    if (!elemento) { setTimeout(() => vivo() && setFase({ tipo: 'meio', texto }), 600); return; }
    setFase({ tipo: 'buscando' });
    (async () => {
      await new Promise(r => setTimeout(r, 250));   // a tela nova monta
      if (alvo.antes) await new Promise(r => setTimeout(r, 400));   // folhas e menus abrindo (animação)
      let r = await acharPonto(elemento, { w: W, h: H });
      if (r && alvo.antes) { await new Promise(x => setTimeout(x, 350)); r = (await acharPonto(elemento, { w: W, h: H }, 600)) ?? r; }   // mede de novo, já parado
      if (!vivo()) return;
      if (!r) { setFase({ tipo: 'meio', texto, aviso: t('Não achei este ponto na tela agora. Confira se a tela certa está aberta e tente de novo.') }); return; }
      // r está na janela; o balão (num Modal, por cima das folhas abertas) desconta a própria origem
      setFase({ tipo: 'achou', texto, r });
    })();
  }, [pedido?.n]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Voltar ao guia" some sozinho depois de um tempo
  useEffect(() => { if (!volta) return; const i = setTimeout(() => setVolta(null), 10000); return () => clearTimeout(i); }, [volta]);

  const fechar = () => {
    const v = pedido?.volta ?? null;
    vez.current++;
    setFase(null);
    fecharPedido();
    if (v) setVolta(v);
  };

  return (
    <View ref={raiz} pointerEvents="box-none" collapsable={false} style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }}>
      {fase?.tipo === 'buscando' && (
        <View pointerEvents="none" style={{ position: 'absolute', left: 0, right: 0, bottom: 130, alignItems: 'center' }}>
          <Text style={{ backgroundColor: C.card, color: C.sub, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 16, overflow: 'hidden', fontSize: 13 }}>{t('Procurando na tela…')}</Text>
        </View>
      )}
      {/* num Modal transparente: assim o balão fica por cima também das folhas (Sheet) e menus abertos */}
      {(fase?.tipo === 'achou' || fase?.tipo === 'meio') && (
        <Modal visible transparent animationType="none" statusBarTranslucent navigationBarTranslucent onRequestClose={fechar} supportedOrientations={['portrait', 'landscape']}>
          {fase.tipo === 'achou' ? <Balao r={fase.r} texto={fase.texto} cor={accent} onFechar={fechar} />
            : <Balao texto={fase.texto} aviso={fase.aviso} cor={accent} onFechar={fechar} />}
        </Modal>
      )}
      {fase?.tipo === 'bloqueada' && (
        <Modal visible animationType="slide" presentationStyle="fullScreen" onRequestClose={fechar}>
          <Paywall id={fase.aba} onBack={() => { vez.current++; setFase(null); fecharPedido(); }} />
        </Modal>
      )}
      {!!volta && !fase && (
        <View pointerEvents="box-none" style={{ position: 'absolute', left: 0, right: 0, bottom: 130, alignItems: 'center' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: C.card, borderRadius: 22, borderWidth: 1, borderColor: accent,
            paddingLeft: 6, paddingRight: 4, elevation: 8, shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: 8, shadowOffset: { width: 0, height: 3 } }}>
            <Pressable onPress={() => { const v = volta; setVolta(null); voltarAoGuia(v); }} hitSlop={6} style={{ paddingHorizontal: 10, paddingVertical: 10 }}>
              <Text style={{ color: accent, fontWeight: '800' }}>‹ {t('Voltar ao guia')}</Text>
            </Pressable>
            <Pressable onPress={() => setVolta(null)} hitSlop={8} accessibilityLabel={t('Fechar')} style={{ paddingHorizontal: 10, paddingVertical: 10 }}>
              <Text style={{ color: C.sub, fontSize: 15 }}>✕</Text>
            </Pressable>
          </View>
        </View>
      )}
    </View>
  );
}

/** Setinha + balão. Com r: em volta do ponto (o resto escurece); sem r: no meio da tela. */
function Balao({ r, texto, aviso, cor, onFechar }: { r?: Retangulo; texto: string; aviso?: string; cor: string; onFechar: () => void }) {
  const { width: W, height: H } = useWindowDimensions();
  const [area, setArea] = useState({ w: W, h: H });
  const caixa = useRef<View>(null);
  const [origem, setOrigem] = useState<{ x: number; y: number } | null>(null);   // onde este balão começa na janela
  const pulo = useRef(new Animated.Value(0)).current;
  const entra = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(entra, { toValue: 1, duration: 220, useNativeDriver: NATIVO }).start();
    const a = Animated.loop(Animated.sequence([
      Animated.timing(pulo, { toValue: 1, duration: 450, easing: Easing.inOut(Easing.quad), useNativeDriver: NATIVO }),
      Animated.timing(pulo, { toValue: 0, duration: 450, easing: Easing.inOut(Easing.quad), useNativeDriver: NATIVO }),
    ]));
    a.start();
    return () => a.stop();
  }, [pulo, entra]);
  const escuro = 'rgba(0,0,0,0.55)';
  const larg = Math.min(area.w - 32, 340);
  const conteudo = (
    <Animated.View style={{ width: larg, backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 10, borderWidth: 1, borderColor: cor, opacity: entra,
      elevation: 10, shadowColor: '#000', shadowOpacity: 0.35, shadowRadius: 10, shadowOffset: { width: 0, height: 4 } }}>
      {!!texto && <Text style={{ color: C.text, fontSize: 15, lineHeight: 21 }}>{texto}</Text>}
      {!!aviso && <Text style={{ color: C.sub, fontSize: 13 }}>{aviso}</Text>}
      <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
        <Pressable onPress={onFechar} style={({ pressed }) => ({ backgroundColor: cor, borderRadius: 18, paddingHorizontal: 18, paddingVertical: 8, opacity: pressed ? 0.8 : 1 })}>
          <Text style={{ color: '#fff', fontWeight: '800' }}>{t('Entendi')}</Text>
        </Pressable>
      </View>
    </Animated.View>
  );
  const medir = (e: { nativeEvent: { layout: { width: number; height: number } } }) => {
    setArea({ w: e.nativeEvent.layout.width, h: e.nativeEvent.layout.height });
    try { caixa.current?.measureInWindow((x, y) => setOrigem({ x: x || 0, y: y || 0 })); } catch { setOrigem({ x: 0, y: 0 }); }
  };

  if (!r) {
    return (
      <View onLayout={medir} style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }}>
        <Pressable onPress={onFechar} accessibilityLabel={t('Fechar')} style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: escuro }} />
        <View pointerEvents="box-none" style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 }}>{conteudo}</View>
      </View>
    );
  }
  if (!origem) return <View ref={caixa} collapsable={false} onLayout={medir} style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }} />;
  const pad = 6;
  // o ponto pode estar parcialmente fora da tela: limita ao que aparece
  const x = Math.max(0, r.x - origem.x - pad), y = Math.max(0, r.y - origem.y - pad);
  const w = Math.min(area.w - x, r.w + pad * 2), h = Math.min(area.h - y, r.h + pad * 2);
  const embaixo = y + h / 2 < area.h / 2;   // ponto na metade de cima: balão embaixo
  const centro = Math.max(16, Math.min(area.w - 16, x + w / 2));
  const esqBalao = Math.max(16, Math.min(area.w - 16 - larg, centro - larg / 2));
  const seta = 28;
  const desloc = pulo.interpolate({ inputRange: [0, 1], outputRange: [0, embaixo ? 6 : -6] });
  const fora = (s: ViewStyle) => <Pressable onPress={onFechar} accessible={false} style={[{ position: 'absolute', backgroundColor: escuro }, s]} />;
  return (
    <View ref={caixa} collapsable={false} onLayout={medir} style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }}>
      {fora({ left: 0, right: 0, top: 0, height: y })}
      {fora({ left: 0, right: 0, top: y + h, bottom: 0 })}
      {fora({ left: 0, width: x, top: y, height: h })}
      {fora({ left: x + w, right: 0, top: y, height: h })}
      {/* o próprio ponto: toque fecha (não aciona o botão por baixo sem querer) */}
      <Pressable onPress={onFechar} accessibilityLabel={t('Fechar')}
        style={{ position: 'absolute', left: x, top: y, width: w, height: h, borderRadius: 12, borderWidth: 3, borderColor: cor }} />
      <Animated.Text pointerEvents="none" style={{ position: 'absolute', left: centro - seta / 2, width: seta, textAlign: 'center', fontSize: seta, color: cor, fontWeight: '900',
        top: embaixo ? y + h + 2 : y - seta - 8, transform: [{ translateY: desloc }],
        textShadowColor: 'rgba(0,0,0,0.5)', textShadowRadius: 4, textShadowOffset: { width: 0, height: 1 } }}>{embaixo ? '▲' : '▼'}</Animated.Text>
      <View pointerEvents="box-none" style={[{ position: 'absolute', left: esqBalao }, embaixo ? { top: Math.min(area.h - 180, y + h + seta + 10) } : { bottom: Math.min(area.h - 180, area.h - y + seta + 10) }]}>
        {conteudo}
      </View>
    </View>
  );
}
